/**
 * Sprint Entity Types (Enhanced based on monday.com)
 * Comprehensive sprint management with goals, metrics, and insights
 */

export type SprintStatus = 'NOT_STARTED' | 'ACTIVE' | 'COMPLETED';

export interface Sprint {
  id: string;
  name: string;
  goal?: string;
  startDate: string; // ISO 8601
  endDate: string; // ISO 8601
  boardId: string;
  status: SprintStatus;
  isActive: boolean; // Green light indicator
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;

  // Sprint metrics
  totalStoryPoints?: number;
  completedStoryPoints?: number;
  plannedStoryPoints?: number;
  unplannedStoryPoints?: number;

  // Task counts
  totalTasks?: number;
  completedTasks?: number;
  inProgressTasks?: number;
  todoTasks?: number;

  // Velocity
  velocity?: number; // Story points completed
}

export interface SprintGoal {
  id: string;
  sprintId: string;
  description: string;
  targetValue?: number;
  currentValue?: number;
  isCompleted: boolean;
  completed: boolean;
  order: number;
  createdAt: string;
}

export interface SprintTask {
  id: string;
  taskId: string;
  sprintId: string;
  name: string;
  description?: string;
  status: string;
  priority?: string;
  isPlanned: boolean; // true = planned at sprint start, false = added during sprint
  storyPoints?: number;
  estimatedHours?: number;
  assigneeId?: string;
  assigneeName?: string;
  assignedAt: string;
  completedAt?: string;
  version?: number;
  position?: number;
}

export interface BurndownDataPoint {
  date: string;
  ideal: number; // Ideal remaining work
  actual: number; // Actual remaining work
  scope: number; // Total scope (can change)
}

export interface BurndownChart {
  sprintId: string;
  dataPoints: BurndownDataPoint[];
  unit: 'STORY_POINTS' | 'TASKS' | 'HOURS';
  totalScope: number;
  remainingWork: number;
  completedWork: number;
}

export interface VelocityData {
  sprintId: string;
  sprintName: string;
  completedStoryPoints: number;
  committedStoryPoints: number;
  completionRate: number; // percentage
  endDate: string;
}

export interface PlannedVsUnplannedData {
  sprintId: string;
  sprintName: string;
  plannedTasks: number;
  unplannedTasks: number;
  plannedStoryPoints: number;
  unplannedStoryPoints: number;
  planningAccuracy: number; // percentage
}

export interface SprintInsights {
  velocity: VelocityData[];
  plannedVsUnplanned: PlannedVsUnplannedData[];
  averageVelocity: number;
  averagePlanningAccuracy: number;
  predictability?: number;
  bestVelocity?: number;
  completionRate?: number;
  velocityTrend?: 'increasing' | 'stable' | 'decreasing';
}

export interface SprintOverview {
  sprint: Sprint;
  goals: Array<{
    id: string;
    description: string;
    targetValue?: number;
    currentValue?: number;
    isCompleted: boolean;
  }>;
  tasks: SprintTask[];
  updates: Array<{
    id: string;
    authorName: string;
    content: string;
    createdAt: string;
  }>;
  activities: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
  files: Array<{
    id: string;
    name: string;
    size: number;
    uploadedAt: string;
  }>;
  teamMembers?: Array<{
    id: string;
    name: string;
  }>;
}

export interface SprintUpdate {
  id: string;
  sprintId: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
}

export interface ActivityLogEntry {
  id: string;
  sprintId: string;
  userId: string;
  userName: string;
  action: string;
  details: string;
  createdAt: string;
}

export interface SprintFile {
  id: string;
  sprintId: string;
  fileName: string;
  fileUrl: string;
  uploadedBy: string;
  uploadedAt: string;
}

// DTOs for API requests
export interface CreateSprintDTO {
  name: string;
  goal?: string;
  startDate: string;
  endDate: string;
  boardId: string;
  goals?: string[]; // Array of goal descriptions
}

export interface UpdateSprintDTO {
  name?: string;
  goal?: string;
  startDate?: string;
  endDate?: string;
  status?: SprintStatus;
}

export interface CompleteSprintDTO {
  incompleteTasks?: {
    taskId: string;
    moveToSprintId?: string; // If null, stays in current sprint
  }[];
}

export interface MoveTasksDTO {
  taskIds: string[];
  targetSprintId: string;
  isPlanned?: boolean;
}

// Redux State
export interface SprintState {
  sprints: Sprint[];
  currentSprint: Sprint | null;
  activeSprint: Sprint | null;
  sprintOverview: SprintOverview | null;
  burndownData: BurndownDataPoint[] | null;
  velocityData: VelocityData[] | null;
  plannedVsUnplannedData: {
    sprints: Array<{planned: number; unplanned: number; sprintName: string}>;
    averageUnplannedRatio: number;
  } | null;
  insights: SprintInsights | null;
  kanbanBoard: KanbanBoard | null;
  automations: SprintAutomation[];
  loading: boolean;
  error: string | null;
}

// Kanban View Types
export interface KanbanColumn {
  id: string;
  name: string;
  title: string;
  status: string;
  tasks: SprintTask[];
  order: number;
}

export interface KanbanBoard {
  sprintId: string;
  columns: KanbanColumn[];
}

// Sprint Automation Types
export interface SprintAutomation {
  id: string;
  name: string;
  trigger: 'SPRINT_START' | 'SPRINT_END' | 'TASK_COMPLETE' | 'DAILY';
  action: string;
  conditions?: AutomationCondition[];
  enabled: boolean;
}

export interface AutomationCondition {
  field: string;
  operator: 'EQUALS' | 'GREATER_THAN' | 'LESS_THAN' | 'CONTAINS';
  value: any;
}

// Helper functions
export const getSprintDuration = (sprint: Sprint): number => {
  const start = new Date(sprint.startDate);
  const end = new Date(sprint.endDate);
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
};

export const getSprintProgress = (sprint: Sprint): number => {
  if (!sprint.totalTasks || sprint.totalTasks === 0) return 0;
  return Math.round((sprint.completedTasks || 0) / sprint.totalTasks * 100);
};

export const getSprintDaysRemaining = (sprint: Sprint): number => {
  const end = new Date(sprint.endDate);
  const now = new Date();
  const remaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, remaining);
};

export const isSprintActive = (sprint: Sprint): boolean => {
  const now = new Date();
  const start = new Date(sprint.startDate);
  const end = new Date(sprint.endDate);
  return sprint.isActive && now >= start && now <= end;
};

export const canDeleteSprint = (sprint: Sprint): boolean => {
  return sprint.status === 'NOT_STARTED';
};

export const canResetSprint = (sprint: Sprint): boolean => {
  return sprint.status === 'ACTIVE';
};

export const formatSprintDates = (sprint: Sprint): string => {
  const start = new Date(sprint.startDate).toLocaleDateString();
  const end = new Date(sprint.endDate).toLocaleDateString();
  return `${start} - ${end}`;
};

export type SprintHealth = 'on-track' | 'at-risk' | 'delayed';

/**
 * Compares work completed so far against time elapsed in the sprint window.
 * Only meaningful for an ACTIVE sprint — null otherwise. A sprint tracking
 * close to (or ahead of) the pace implied by elapsed time is "on-track";
 * meaningfully behind is "at-risk"; far behind (or already past end date
 * without being done) is "delayed".
 */
export const getSprintHealth = (
  sprint: Sprint,
  progressPercent: number
): SprintHealth | null => {
  if (sprint.status !== 'ACTIVE') return null;

  const start = new Date(sprint.startDate).getTime();
  const end = new Date(sprint.endDate).getTime();
  const now = Date.now();
  const totalDuration = Math.max(1, end - start);
  const elapsedPercent = Math.min(100, Math.max(0, ((now - start) / totalDuration) * 100));

  const gap = progressPercent - elapsedPercent; // negative = behind schedule
  if (gap >= -10) return 'on-track';
  if (gap >= -25) return 'at-risk';
  return 'delayed';
};
